import { useState, useEffect, useCallback, useMemo } from "react";
import { X, Printer } from "lucide-react";
import { toast } from "sonner";
import clienteApi from "@/lib/clienteApi";
import { SearchableSelect } from "@/components/ui/searchable-select";

/**
 * Modal "Relatório de Autorizações por Autorizador".
 *
 * Fluxo:
 *  1. Seleciona filtro (Todos | Apenas Ativos)
 *  2. Carrega lista de autorizadores conforme filtro (com selo de situação
 *     inativa/vencida/bloqueada ao lado do nome)
 *  3. Clica em "Listar" → abre janela nova imprimível com o relatório completo
 *
 * Colunas do relatório: Status, EQL, Nome, Empresa, Setor, Vlde. Inicial, Vlde. Final.
 * Cabeçalho do relatório inclui: título, data/hora, operador atual e nome do autorizador.
 */
export default function RelatorioAutorizacoesPorAutorizadorModal({ open, onOpenChange, operatorNome, clienteNome }) {
  const [filtro, setFiltro] = useState("todos"); // "todos" | "ativos"
  const [autorizadores, setAutorizadores] = useState([]);
  const [autorizadorId, setAutorizadorId] = useState("");
  const [loading, setLoading] = useState(false);
  const [gerando, setGerando] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAutorizadorId("");
    (async () => {
      setLoading(true);
      try {
        const r = await clienteApi.get(`/cliente/relatorios/autorizadores-lista?filtro=${filtro}`);
        setAutorizadores(Array.isArray(r.data) ? r.data : []);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Erro ao carregar autorizadores");
        setAutorizadores([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, filtro]);

  const autorizadorSelecionado = useMemo(
    () => autorizadores.find((a) => a.id === autorizadorId) || null,
    [autorizadorId, autorizadores]
  );

  const situacaoLabel = (sit) => ({
    ok: "",
    vencido: "VENCIDO",
    bloqueado: "BLOQUEADO",
    inativo: "INATIVO",
  }[sit] || "");

  const situacaoBadgeClass = (sit) => ({
    vencido: "bg-red-500/20 text-red-200 border-red-400/40",
    bloqueado: "bg-orange-500/20 text-orange-200 border-orange-400/40",
    inativo: "bg-red-500/20 text-red-200 border-red-400/40",
  }[sit] || "");

  const fmt = (d) => {
    if (!d) return "—";
    try {
      const only = String(d).split("T")[0];
      const [y, mo, dy] = only.split("-");
      return `${dy}/${mo}/${y}`;
    } catch { return String(d); }
  };

  const gerar = useCallback(async () => {
    if (!autorizadorId) { toast.error("Selecione um autorizador"); return; }
    setGerando(true);
    try {
      const r = await clienteApi.get(
        `/cliente/relatorios/autorizadores/${autorizadorId}/autorizados?filtro=${filtro}`
      );
      const dados = r.data || {};
      const linhas = dados.linhas || [];
      const aut = dados.autorizador || {};
      const okCount = linhas.filter((x) => x.status === "ok").length;

      const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

      const statusBadge = (s) => {
        const map = {
          ok:        { txt: "OK", cls: "b-ok" },
          vencido:   { txt: "AUTORIZADOR VENCIDO", cls: "b-blq" },
          bloqueado: { txt: "AUTORIZADOR BLOQUEADO", cls: "b-blq" },
          inativo:   { txt: "INATIVO", cls: "b-blq" },
          vencida:   { txt: "AUTORIZAÇÃO VENCIDA", cls: "b-blq" },
          inativa:   { txt: "AUTORIZAÇÃO INATIVA", cls: "b-blq" },
        };
        const m = map[s] || { txt: String(s || "").toUpperCase(), cls: "b-blq" };
        return `<span class="badge ${m.cls}">${esc(m.txt)}</span>`;
      };

      const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="utf-8" />
<title>Autorizações por Autorizador — ${esc(aut.nome || "")}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; margin: 0; padding: 24px; color: #1f2937; background: #fff; }
  header { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; border-bottom: 2px solid #0f172a; padding-bottom: 12px; margin-bottom: 12px; }
  header h1 { margin: 0 0 4px 0; font-size: 20px; letter-spacing: 0.3px; }
  header .sub { font-size: 12px; color: #475569; }
  .autoriz-line { font-size: 13px; margin: 8px 0 14px 0; padding: 8px 12px; border-left: 4px solid #0f172a; background: #f8fafc; }
  .autoriz-line strong { color: #0f172a; }
  .meta { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px 24px; font-size: 12px; margin-bottom: 14px; }
  .meta div span { color: #64748b; font-weight: 600; margin-right: 4px; }
  table { width: 100%; border-collapse: collapse; font-size: 11.5px; }
  thead th { background: #0f172a; color: #f1f5f9; text-align: left; padding: 8px 10px; text-transform: uppercase; letter-spacing: 0.4px; font-size: 10px; }
  tbody td { padding: 7px 10px; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
  tbody tr:nth-child(even) { background: #f8fafc; }
  .badge { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 10px; font-weight: 700; letter-spacing: 0.5px; }
  .b-ok { background: #dcfce7; color: #166534; }
  .b-blq { background: #fee2e2; color: #991b1b; }
  .badge-sit { display: inline-block; margin-left: 8px; padding: 2px 8px; border-radius: 4px; font-size: 10px; font-weight: 700; background: #fee2e2; color: #991b1b; }
  .footer { margin-top: 18px; font-size: 10px; color: #64748b; text-align: right; }
  .toolbar { position: fixed; top: 12px; right: 12px; display: flex; gap: 8px; }
  .toolbar button { padding: 8px 14px; font-size: 13px; font-weight: 700; border: none; border-radius: 6px; cursor: pointer; box-shadow: 0 2px 4px rgba(0,0,0,0.15); }
  .btn-print { background: #0f172a; color: #fff; }
  .btn-close { background: #e2e8f0; color: #0f172a; }
  @media print {
    body { padding: 0; }
    .toolbar { display: none; }
    thead th { background: #0f172a !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    tbody tr:nth-child(even) { background: #f8fafc !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
</style>
</head>
<body>
<div class="toolbar">
  <button class="btn-print" onclick="window.print()">🖨 Imprimir / PDF</button>
  <button class="btn-close" onclick="window.close()">Fechar</button>
</div>
<header>
  <div>
    <h1>Relatório de Autorizações por Autorizador</h1>
    <div class="sub">Cadastros e autorizações adicionais vinculados ao autorizador selecionado.</div>
  </div>
  <div style="text-align:right; font-size:11px; color:#475569;">
    <div>Emitido em ${new Date().toLocaleString("pt-BR")}</div>
    <div>Operador: <strong>${esc(operatorNome || "—")}</strong></div>
    <div>Cliente: ${esc(clienteNome || "—")}</div>
  </div>
</header>
<div class="autoriz-line">
  <strong>Autorizador:</strong> ${esc(aut.nome || "")}
  ${aut.matricula ? ` &nbsp;·&nbsp; matr. ${esc(aut.matricula)}` : ""}
  ${aut.eql ? ` &nbsp;·&nbsp; EQL ${esc(aut.eql)}` : ""}
  ${aut.tipo_cartao_descricao ? ` &nbsp;·&nbsp; ${esc(aut.tipo_cartao_descricao)}` : ""}
  ${aut.tipo_autorizador ? ` &nbsp;·&nbsp; <strong>Autorizante ${aut.tipo_autorizador === "MASTER" ? "Master" : "Secundário"}</strong>` : ""}
  ${aut.situacao && aut.situacao !== "ok" ? `<span class="badge-sit">${esc(String(aut.situacao).toUpperCase())}</span>` : ""}
</div>
<div class="meta">
  <div><span>Filtro:</span> ${filtro === "ativos" ? "Apenas ATIVOS" : "TODOS"}</div>
  <div><span>Total de cadastros:</span> ${linhas.length}</div>
  <div><span>Habilitados (OK):</span> ${okCount}</div>
  <div><span>Bloqueados:</span> ${linhas.length - okCount}</div>
</div>
<table>
  <thead>
    <tr>
      <th>Status</th>
      <th>EQL</th>
      <th>Nome</th>
      <th title="É Autorizador (Secundário)">A</th>
      <th>Empresa</th>
      <th>Setor</th>
      <th>Validade Inicial</th>
      <th>Validade Final</th>
    </tr>
  </thead>
  <tbody>
    ${linhas.length === 0 ? `<tr><td colspan="8" style="text-align:center; padding:16px; color:#64748b;">Nenhum cadastro encontrado para este autorizador com o filtro selecionado.</td></tr>` : linhas.map((r) => `
      <tr>
        <td>${statusBadge(r.status)}</td>
        <td style="font-family: monospace; font-weight:700;">${esc(r.eql)}</td>
        <td>${esc(r.nome)}${r.origem === "adicional" ? ' <span class="badge b-blq" style="background:#dbeafe; color:#1e40af; margin-left:6px;">ADICIONAL</span>' : ""}</td>
        <td style="text-align:center; font-weight:700;">${r.autoriza_visitas ? '<span class="badge" style="background:#e0e7ff; color:#3730a3;">AS</span>' : '<span style="color:#64748b;">Não</span>'}</td>
        <td>${esc(r.empresa) || "—"}</td>
        <td>${esc(r.setor) || "—"}</td>
        <td>${esc(fmt(r.validade_inicial))}</td>
        <td>${esc(fmt(r.validade_final))}</td>
      </tr>
    `).join("")}
  </tbody>
</table>
<div class="footer">SGA Brave Access · Relatório gerado automaticamente</div>
<script>window.onload = () => setTimeout(() => window.focus(), 100);</script>
</body>
</html>`;

      const w = window.open("", "_blank", "width=1200,height=800");
      if (!w) {
        toast.error("Não foi possível abrir a janela do relatório. Verifique o bloqueador de pop-up.");
        return;
      }
      w.document.open();
      w.document.write(html);
      w.document.close();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao gerar relatório");
    } finally {
      setGerando(false);
    }
  }, [autorizadorId, filtro, operatorNome, clienteNome, onOpenChange]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="modal-rel-autoriz-por-autoriz">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => onOpenChange(false)}></div>
      <div className="relative bg-brand-dark border border-brand-ice/20 rounded-xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-3 border-b border-brand-ice/20">
          <h3 className="text-base font-heading font-bold text-brand-ice">
            Relatório de Autorizações por Autorizador
          </h3>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="p-1.5 rounded-lg text-brand-ice/60 hover:text-brand-ice hover:bg-white/10 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="text-xs text-brand-ice/70 rounded-lg border border-brand-ice/10 bg-white/[0.03] px-3 py-2">
            Escolha o filtro de estado e o autorizador a listar. O relatório abrirá em janela nova com opções de imprimir e salvar em PDF.
          </div>

          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-wider font-bold text-brand-ice/60">Cadastros a listar</label>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="filtro-todos"
                onClick={() => setFiltro("todos")}
                className={`flex-1 px-3 py-2 rounded-lg text-xs font-heading font-bold uppercase tracking-wider border transition-colors ${filtro === "todos" ? "bg-brand-medium text-white border-brand-light" : "bg-white/5 text-brand-ice/70 border-brand-ice/20 hover:bg-white/10"}`}
              >
                Todos
              </button>
              <button
                type="button"
                data-testid="filtro-ativos"
                onClick={() => setFiltro("ativos")}
                className={`flex-1 px-3 py-2 rounded-lg text-xs font-heading font-bold uppercase tracking-wider border transition-colors ${filtro === "ativos" ? "bg-brand-medium text-white border-brand-light" : "bg-white/5 text-brand-ice/70 border-brand-ice/20 hover:bg-white/10"}`}
              >
                Apenas Ativos
              </button>
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-wider font-bold text-brand-ice/60">Autorizador</label>
            {/* v3.8.7 — SearchableSelect com input de busca embutido. Antes
                era <select> nativo — lento e sem filtro por digitação em
                condomínios com 200+ autorizadores. `options.label` inclui
                nome + matrícula + EQL + situação para o operador identificar
                mesmo com homônimos.  */}
            <SearchableSelect
              data-testid="select-autorizador"
              value={autorizadorId}
              onValueChange={setAutorizadorId}
              placeholder={loading ? "Carregando..." : "Digite para buscar..."}
              clearable
              options={autorizadores.map((a) => {
                const sit = situacaoLabel(a.situacao);
                const eqlsTxt = (a.eqls && a.eqls.length) ? a.eqls.join(" · ") : (a.eql || "");
                const matsTxt = (a.matricula_ids && a.matricula_ids.length > 1)
                  ? `${a.matricula_ids.length} matr.`
                  : (a.matricula ? `matr. ${a.matricula}` : "");
                const label = `${a.nome}${matsTxt ? ` — ${matsTxt}` : ""}${eqlsTxt ? ` (${eqlsTxt})` : ""}${sit ? ` — ${sit}` : ""}`;
                return { value: a.id, label };
              })}
            />
            {autorizadorSelecionado && autorizadorSelecionado.situacao !== "ok" && (
              <span
                data-testid="autoriz-situacao-badge"
                className={`inline-flex items-center gap-1 text-[10px] font-heading font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md border mt-1 ${situacaoBadgeClass(autorizadorSelecionado.situacao)}`}
              >
                {situacaoLabel(autorizadorSelecionado.situacao)}
              </span>
            )}
          </div>
        </div>

        <div className="px-5 py-3 border-t border-brand-ice/20 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="px-3 py-1.5 rounded-lg text-brand-ice/70 hover:text-brand-ice hover:bg-white/10 text-sm transition-colors"
          >
            Cancelar
          </button>
          <button
            type="button"
            data-testid="btn-listar-relatorio"
            onClick={gerar}
            disabled={!autorizadorId || gerando}
            className="flex items-center gap-2 bg-brand-medium hover:bg-brand-dark text-white px-4 py-1.5 rounded-lg font-heading font-bold text-sm transition-colors disabled:opacity-60"
          >
            <Printer className="w-3.5 h-3.5" />
            {gerando ? "Gerando..." : "Listar"}
          </button>
        </div>
      </div>
    </div>
  );
}
